/**
 * I valori del disegno, estratti dal prototipo v3 (`Palco Streaming App v3.dc.html`).
 *
 * Non sono scelte mie: sono le misure e i colori che stanno negli stili in linea del
 * prototipo, disegnato a 1920 × 1080. Stanno qui in un posto solo perché il markup del
 * prototipo è codice di un attrezzo di prototipazione e non entra in questo repository —
 * di lì si prende il **disegno**, non il markup.
 *
 * Il marchio dell'operatore (nome, logo, palette) arriva dal gateway all'accesso, in
 * base64 e non come indirizzi (02-ACCESSO §6). Questi sono i valori neutri: quelli della
 * schermata di accesso, che è **l'unica che non può essere marchiata**, e il punto di
 * partenza che il marchio sovrascrive.
 */

/**
 * La pila dei caratteri del disegno.
 *
 * `index.html` mette già **Onest** sul `body`, ma ogni stile in linea che scrive
 * `font: '600 21px sans-serif'` lo **sovrascrive** — la forma abbreviata `font` reimposta anche la
 * famiglia. Risultato: il carattere del prototipo non è mai comparso, e l'app somigliava al disegno
 * senza esserlo. Va scritto in ogni `font`, e per questo sta qui.
 */
export const FONT_STACK = 'Onest, system-ui, sans-serif';

export const colors = {
  background: '#0A0D13',
  panel: '#0D1118',
  /** Il fondo della colonna del menu: nel prototipo è più scuro dello sfondo. */
  railBackground: '#0D1118',
  /** Il tondo dell'iniziale nel blocco del profilo, in fondo alla colonna. */
  avatarBackground: '#2A3444',
  /** Il fondo del lettore: più scuro dello sfondo dell'app, perché lì non c'è interfaccia. */
  playerBackground: '#05070A',
  surface: '#141922',
  surfaceProgram: '#151B25',
  surfaceProgramLive: '#232D3D',
  surfaceRaised: '#1C2330',
  surfaceButton: '#232B38',
  border: '#2A3342',
  borderFaint: 'rgba(255,255,255,.06)',
  text: '#F2F4F7',
  textMuted: '#C9D0DB',
  textDim: '#A3ACBA',
  textFaint: '#7C8696',
  accent: '#FFB547',
  focusProgram: '#33405A',
  live: '#E23B30',
  liveBright: '#FF5A4E',
  errorText: '#FF6B61',
  errorPanel: '#2A1215',
  success: '#3DDC97',
  successPanel: '#0F2419',
  info: '#34507A',
} as const;

export const layout = {
  /** Il disegno è a 1920 × 1080: tutte le misure sono in quel sistema, e la scena si
   *  ridimensiona per intero invece di riadattarsi. */
  designWidth: 1920,
  designHeight: 1080,
  /** Menu laterale, presente ovunque tranne che nel lettore e nell'accesso. */
  sidebarWidth: 280,
  /**
   * Colonna delle categorie nella guida.
   *
   * 290 nel prototipo, che con i nomi veri di un fornitore taglia quasi tutto: «Intrattenimento»,
   * «Documentari e cultura», «Sport — Calcio estero» non ci stanno. I 70 px in più vengono dalla
   * riga del tempo, che se li può permettere.
   */
  guideCategoryWidth: 360,
  /**
   * Colonna del canale (logo + numero + nome) dentro la griglia.
   *
   * Con 260 il nome aveva 132 px: tagliava tutto tranne le sigle. Con 340 ne ha 212, e su due righe
   * regge un nome intero — che è l'informazione essenziale di una guida.
   */
  guideChannelWidth: 340,
  /** Il logo del canale dentro la colonna. */
  guideLogoWidth: 104,
  guideLogoHeight: 66,
  /*
    La larghezza della riga del tempo **non sta qui**.

    Era il numero `990` scritto a mano, e lo stesso numero era scritto in altri tre posti — il
    viewport del controllore nella diretta, quello del banco di misura, e le prove. Quattro copie di
    una misura che dipende da due cose (i minuti della finestra e i pixel al minuto): cambiarne una
    ne lasciava indietro tre, in silenzio, e il sintomo sarebbe stato un riquadro di selezione
    sfasato dalle celle.

    Si calcola con `windowWidthPx(finestra)`, che è l'unico posto che conosce entrambe.
  */
  guideHeaderHeight: 236,
  guideRulerHeight: 46,
  /** Colonna sinistra della schermata di accesso. */
  loginAsideWidth: 760,
  /** L'intestazione della guida — logo del canale più anteprima — nel prototipo è alta 236. */
  guidePreviewHeight: 170,
  /** Il riquadro del logo nell'anteprima della guida. */
  guidePreviewLogoWidth: 300,
  /** Colonna delle impostazioni: 900 px, il resto va alla scheda che spiega. */
  settingsColumnWidth: 900,
  /** Le caselle dei preferiti: cinque per riga, alte 170. */
  /*
    La casella di un preferito ha la **forma del riquadro della guida**, non una sua.

    Era 170 px su cinque colonne: su uno schermo pieno viene una casella larga il doppio
    dell'altezza, che non somiglia a niente di quel che c'è nel resto dell'app — e un logo dentro un
    rettangolo così basso resta minuscolo in mezzo al vuoto.

    Sette colonne e 136 px danno lo stesso rapporto del riquadro della guida (104×66), che è la
    forma che chi usa l'app ha già negli occhi. E le caselle visibili diventano di più, che in una
    schermata di preferiti è il punto.
  */
  favoriteTileHeight: 136,
  favoriteColumns: 7,
  /** La locandina nelle fasce on demand: 230 × 345 nel prototipo, cioè 2:3. */
  posterTileWidth: 230,
  posterTileHeight: 345,
  /** La piastrella di un episodio: orizzontale, 340 × 191 — 16:9, come un fotogramma. */
  episodeTileWidth: 340,
  episodeTileHeight: 191,
  /**
   * L'anteprima di ripresa sulla scheda: **2:3, come una locandina**.
   *
   * Nel prototipo è 560 × 315, cioè 16:9, e dentro c'è scritto «fotogramma al punto di ripresa». La
   * forma era giusta per quel contenuto — un fermo immagine del minuto in cui ci si era fermati — ma
   * quel fotogramma **non esiste**: non abbiamo un servizio che estragga miniature, e nella casella ci
   * finisce l'unica immagine che c'è, la locandina. Una locandina 2:3 dentro un riquadro 16:9 viene
   * tagliata a fascia orizzontale: si vede una striscia presa in mezzo al poster, col titolo mozzato.
   *
   * È lo stesso difetto corretto nel backend il 30-09-2026 per le immagini degli episodi, e si
   * riconosce allo stesso modo: il campo è pieno, quindi nessuno si accorge che la forma è sbagliata.
   *
   * 300 × 450 e non più grande perché l'area sta dentro i 680 px del riquadro di testa: la scheda non
   * cresce e le stagioni restano dove sono.
   */
  resumePreviewWidth: 300,
  resumePreviewHeight: 450,
} as const;

export const focusRing = {
  widthPx: 4,
  offsetPx: 2,
  color: colors.accent,
} as const;

/**
 * Le transizioni sono una variabile CSS perché l'impostazione «Animazioni» le spegne sul
 * serio: nel prototipo cambia le variabili delle transizioni, delle ombre e delle
 * sfumature, non è un interruttore che non fa niente (07-PRESTAZIONI §5).
 *
 * «Ogni voce deve avere un effetto misurabile. Se spegnere gli effetti non cambia nulla,
 * la voce va tolta: un'impostazione che non fa niente insegna all'utente che le
 * impostazioni non servono.»
 */
export const cssVariables = {
  transition: '--tr',
  focusScale: '--fs',
  /**
   * L'ingrandimento **ridotto**, per gli elementi larghi.
   *
   * Il prototipo usa 1,02 sulle righe delle impostazioni e 1,04 su tutto il resto, e non è una
   * distrazione: una riga larga 900 px ingrandita del 4% sborda oltre la colonna, e il contorno del
   * fuoco finisce sotto la scheda accanto. Sono due variabili perché l'impostazione «Animazioni» deve
   * spegnerle **entrambe** — una sola lasciata accesa è il genere di residuo che fa dire che
   * l'interruttore non funziona.
   */
  focusScaleSubtle: '--fs2',
  /**
   * Il velo sopra la copertina della scheda, e la sfumatura dell'OSD del lettore.
   *
   * Sono le due sfumature a tutta larghezza che l'impostazione «Effetti visivi» deve spegnere: su una
   * Fire TV Stick una sfumatura ridisegnata a ogni fotogramma si sente. Spente diventano **fondi
   * pieni**, non spariscono: senza il velo il titolo bianco finirebbe sopra una foto chiara e non si
   * leggerebbe più.
   */
  coverOverlay: '--ov',
  osdGradient: '--osd',
  /** La sfumatura in fondo alla copertina: con gli effetti spenti non si disegna affatto. */
  coverFade: '--fade',
} as const;

export interface PerformanceSettings {
  readonly animations: boolean;
  readonly visualEffects: boolean;
}

export function performanceCssVariables(settings: PerformanceSettings): Record<string, string> {
  return {
    [cssVariables.transition]: settings.animations ? 'transform .16s ease' : 'none',
    [cssVariables.focusScale]: settings.animations ? '1.04' : '1',
    [cssVariables.focusScaleSubtle]: settings.animations ? '1.02' : '1',
    // Acceso: il velo del prototipo. Spento: più coperto e senza sfumature, perché quel che si toglie
    // è il **disegno della sfumatura**, non la leggibilità del titolo che ci sta sopra.
    [cssVariables.coverOverlay]: settings.visualEffects ? 'rgba(10,13,19,.82)' : 'rgba(10,13,19,.94)',
    [cssVariables.osdGradient]: settings.visualEffects
      ? 'linear-gradient(0deg,rgba(5,7,10,.96) 30%,rgba(5,7,10,0))'
      : 'rgba(5,7,10,.96)',
    [cssVariables.coverFade]: settings.visualEffects
      ? 'linear-gradient(0deg,#0A0D13,rgba(10,13,19,0))'
      : 'none',
  };
}
